import { CustomModal } from '@components';
import { FC } from 'react';
import { useCareManagementActivitiesModal } from './hooks';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';
import { t } from 'i18next';

interface CareManagementActivitiesModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient_id: string;
  confirmAction: () => void;
  isLoading: boolean;
  variant?: 'view' | 'log';
  isEditDisabled?: boolean;
}

export const CareManagementActivitiesModal: FC<
  CareManagementActivitiesModalProps
> = ({
  isOpen,
  onClose,
  patient_id,
  confirmAction,
  isLoading,
  variant = 'log',
  isEditDisabled = false,
}) => {
  const { activities } = useCareManagementActivitiesModal({
    patient_id,
    enabled: isOpen,
  });
  return (
    <CustomModal
      isOpen={isOpen}
      isLoading={isLoading}
      title={t(`careManagementActivitiesModal.title`)}
      onClose={onClose}
    >
      <FormGroup>
        <FormControlLabel
          control={<Checkbox checked={activities?.plan} />}
          label='Care plan update'
          disabled
        />
        <FormControlLabel
          control={<Checkbox checked={activities?.reconciliate} />}
          label='Medication reconciliation'
          disabled
        />
        <FormControlLabel
          control={<Checkbox checked={activities?.coordinate} />}
          label='Care coordination (including referrals and orders)'
          disabled
        />
        <FormControlLabel
          control={<Checkbox checked={activities?.educate} />}
          label='Patient / POA education'
          disabled
        />
        <FormControlLabel
          control={<Checkbox checked={activities?.communicate} />}
          label='Communication for patient requests'
          disabled
        />
      </FormGroup>
      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
        {t(`careManagementActivitiesModal.subText1`)}
      </Typography>
      {variant === 'log' && (
        <>
          <Divider sx={{ height: 1, width: '100%', my: 2 }} />
          <Typography variant='body1'>
            {t(`careManagementActivitiesModal.subText2`)}
          </Typography>
        </>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          my: 3,
        }}
      >
        <Button
          startIcon={
            isLoading && <CircularProgress color='inherit' size={16} />
          }
          variant='contained'
          color='secondary'
          onClick={confirmAction}
          disabled={isLoading || isEditDisabled}
        >
          {isLoading
            ? t('general.loading')
            : t(`careManagementActivitiesModal.confirmButton.${variant}`)}
        </Button>
      </Box>
    </CustomModal>
  );
};
